import { CornerDownRight } from 'lucide-react';
import { Link } from 'react-router-dom';

import { cn } from '@/lib/utils';
import { initials } from '@/modules/debug/linkedin/format';
import {
  replyTargetJob,
  senderKindLabel,
  THREAD_STATUS_LABELS,
} from '@/modules/debug/linkedin/linkedInDebug';
import type { DebugLinkedInThread } from '@/modules/debug/linkedin/types';
import { Avatar, AvatarFallback } from '@/modules/ui/components/avatar/Avatar';
import { Badge } from '@/modules/ui/components/badge/Badge';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/modules/ui/components/tooltip/Tooltip';

type ThreadStatus = DebugLinkedInThread['threadStatus'];

const THREAD_STATUS_CLASSES: Record<ThreadStatus, string> = {
  Active: 'border-green-500 text-green-700',
  Paused: 'border-yellow-500 text-yellow-700',
  ReviewRequired: 'border-red-500 text-red-700',
};

function ThreadStatusBadge({ status }: { status: ThreadStatus }) {
  return (
    <Badge
      variant="outline"
      className={cn('text-xs', THREAD_STATUS_CLASSES[status])}
    >
      {THREAD_STATUS_LABELS[status]}
    </Badge>
  );
}

function SettingChip({
  label,
  value,
  tooltip,
}: {
  label: string;
  value: boolean;
  tooltip?: string;
}) {
  const chip = (
    <span
      className={cn(
        'rounded-full border px-2 py-0.5 text-xs',
        tooltip && 'cursor-help',
        value
          ? 'border-green-400 bg-green-50 text-green-700'
          : 'border-red-300 bg-red-50 text-red-600',
      )}
    >
      {value ? '✓' : '✗'} {label}
    </span>
  );
  if (!tooltip) return chip;
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>{chip}</TooltipTrigger>
        <TooltipContent className="max-w-60 text-xs">{tooltip}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

function IdBadge({ label, value }: { label: string; value: string | null }) {
  return (
    <Badge variant="outline" className="font-mono text-xs">
      {label}: {value ?? '—'}
    </Badge>
  );
}

interface ThreadHeaderProps {
  thread: DebugLinkedInThread;
}

export function ThreadHeader({ thread }: ThreadHeaderProps) {
  const settingsJob = replyTargetJob(thread.candidateJobs);

  return (
    <header className="border-b px-6 py-3">
      <div className="flex items-center gap-3">
        <Avatar className="size-10 bg-[#0a66c2]">
          <AvatarFallback className="bg-[#0a66c2] text-white">
            {initials(thread.candidateName)}
          </AvatarFallback>
        </Avatar>
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-semibold">
            {thread.candidateName}
          </p>
          {thread.candidateHeadline && (
            <p className="text-muted-foreground truncate text-xs">
              {thread.candidateHeadline}
            </p>
          )}
        </div>
        <div className="flex flex-wrap justify-end gap-1">
          <IdBadge label="chat_id" value={thread.externalId} />
          <IdBadge
            label="provider_id"
            value={thread.candidateLinkedInProfileId}
          />
        </div>
      </div>
      <div className="mt-2 flex flex-wrap items-center gap-2">
        {thread.candidateJobs.map((job) => (
          <span
            key={job.candidateJobId}
            className={cn(
              'inline-flex items-center gap-1 rounded-md border px-2 py-0.5 text-xs',
              job.isReplyTarget && 'border-[#0a66c2] bg-[#0a66c2]/5',
            )}
          >
            {job.isReplyTarget && (
              <span
                className="inline-flex items-center gap-0.5 font-medium text-[#0a66c2]"
                title="Candidate replies are filed under this job: the job of the last sent message"
              >
                <CornerDownRight className="size-3" />
                replies go here ·
              </span>
            )}
            <Link
              to={`/jobs/${job.jobId}/candidates/${thread.candidateId}`}
              className="hover:underline"
            >
              {job.jobTitle}
            </Link>
            <span className="text-muted-foreground">· {job.tenantName}</span>
          </span>
        ))}
        {thread.senderKind && (
          <Badge variant="secondary" className="text-xs">
            {senderKindLabel(thread.senderKind)}
          </Badge>
        )}
        <ThreadStatusBadge status={thread.threadStatus} />
        {settingsJob && (
          <>
            <SettingChip
              label="auto follow-up"
              value={settingsJob.isMessagingAutomated}
              tooltip="When enabled, Finlay automatically sends follow-up messages after a candidate replies. Toggle in the job edit page under Messaging settings."
            />
            <SettingChip
              label="auto send outreach"
              value={settingsJob.isReachOutAutomated}
              tooltip="When enabled, the initial outreach message is sent automatically. When off, it is saved as a draft for manual review. Toggle in the job edit page under Messaging settings."
            />
            <SettingChip
              label="invite follow-up"
              value={settingsJob.isInvitationFollowUpEnabled}
              tooltip="Not used in the simulator: every candidate is treated as already connected, so no invitation is sent."
            />
          </>
        )}
      </div>
    </header>
  );
}
